import React, { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
// Layouts
import ServiceLayout from './components/layout/ServiceLayout';

// Service Auth pages
import LoginPage from './pages/service/auth/LoginPage';
import NaverCallback from './pages/service/auth/NaverCallback';
import KakaoCallback from './pages/service/auth/KakaoCallback';
import GoogleCallback from './pages/service/auth/GoogleCallback';
import OAuthCompleteProfile from './pages/service/auth/OAuthCompleteProfile';
import SignupPage from './pages/service/auth/SignupPage';
import PasswordResetPage from './pages/service/auth/PasswordResetPage';

// Transfer pages
import TransferAirport from './pages/service/transfer/TransferAirport';
import TransferGolf from './pages/service/transfer/TransferGolf';
import TransferSki from './pages/service/transfer/TransferSki';
import TransferHospital from './pages/service/transfer/TransferHospital';
import TransferPet from './pages/service/transfer/TransferPet';
import TransferBusiness from './pages/service/transfer/TransferBusiness';
import BookingSettings from './pages/service/transfer/BookingSettings';
import TransferPayment from './pages/service/transfer/TransferPayment';
import TransferComplete from './pages/service/transfer/TransferComplete';

// Tour pages
import Tour from './pages/service/tour/Tour';
import TourDetail from './pages/service/tour/TourDetail';
import TourBookingInfo from './pages/service/tour/TourBookingInfo';
import TourPayment from './pages/service/tour/TourPayment';
import TourComplete from './pages/service/tour/TourComplete';

// Reservation pages
import ReservationList from './pages/service/reservation/ReservationList';
import ReservationDetail from './pages/service/reservation/ReservationDetail';
import GuestReservation from './pages/service/reservation/GuestReservation';
import SurchargePayment from './pages/service/reservation/SurchargePayment';

// MyPage
import MyAccount from './pages/service/mypage/MyAccount';
import MyReviews from './pages/service/mypage/MyReviews';
import AccountEdit from './pages/service/mypage/AccountEdit';

// Info pages
import About from './pages/service/info/About';
import FAQ from './pages/service/info/FAQ';
import Partnership from './pages/service/info/Partnership';
import Reviews from './pages/service/info/Reviews';
import Shopping from './pages/service/info/Shopping';
import Inquiry from './pages/service/info/Inquiry';
import NotFound from './pages/service/info/NotFound';
import PolicyPage from './pages/service/policy/PolicyPage';

// 어드민 전 영역은 분리 번들로 지연 로드 — 공개 페이지 초기 번들에서 제외(CWV).
// 어드민은 prerender 대상이 아니라 hydration 영향 없음.
const AdminRoutes = lazy(() => import('./AdminRoutes'));

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function App({ basename = '' }) {
  return (
    <BrowserRouter basename={basename}>
      <ScrollToTop />
      <Routes>
        {/* Auth */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/login" element={<Navigate to="/login" replace />} />
        <Route path="/auth/kakao/callback" element={<KakaoCallback />} />
        <Route path="/auth/naver/callback" element={<NaverCallback />} />
        <Route path="/auth/google/callback" element={<GoogleCallback />} />
        <Route path="/auth/complete-profile" element={<OAuthCompleteProfile />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/reset-password" element={<PasswordResetPage />} />
        <Route path="/auth/reset-password" element={<Navigate to="/reset-password" replace />} />

        {/* Admin — 지연 로드 분리 번들 (/admin/* 전체) */}
        <Route
          path="/admin/*"
          element={(
            <Suspense fallback={<div style={{ minHeight: '100vh' }} />}>
              <AdminRoutes />
            </Suspense>
          )}
        />

        {/* Service (with Navbar + Footer) */}
        <Route element={<ServiceLayout />}>
          {/* #SEO 홈/공항 중복 해소: 루트가 공항 랜딩을 직접 렌더(리다이렉트 제거).
              /transfer/airport 는 canonical 을 / 로 통합(SEO.js CANONICAL_ALIAS)해 중복 색인 방지. */}
          <Route path="/" element={<TransferAirport />} />

          {/* Transfer landing pages */}
          <Route path="/transfer/airport" element={<TransferAirport />} />
          <Route path="/transfer/golf" element={<TransferGolf />} />
          {/* #244: 리조트 이동 랜딩 URL 을 /transfer/ski → /transfer/resort 로 변경(SEO).
              기존 URL 은 클라이언트 리다이렉트(+ nginx 301)로 처리. 부킹/결제·BE 내부 타입은 'ski' 그대로(무회귀). */}
          <Route path="/transfer/resort" element={<TransferSki />} />
          <Route path="/transfer/ski" element={<Navigate to="/transfer/resort" replace />} />
          <Route path="/transfer/hospital" element={<TransferHospital />} />
          <Route path="/transfer/pet" element={<TransferPet />} />
          <Route path="/transfer/business" element={<TransferBusiness />} />

          {/* Tour */}
          <Route path="/tour" element={<Tour />} />
          <Route path="/tour/:id" element={<TourDetail />} />

          {/* Info */}
          <Route path="/about" element={<About />} />
          <Route path="/faq" element={<FAQ />} />
          <Route path="/inquiry" element={<Inquiry />} />
          <Route path="/partnership" element={<Partnership />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/shopping" element={<Shopping />} />
          <Route path="/policy/:slug" element={<PolicyPage />} />

          {/* Reservation */}
          <Route path="/reservation" element={<ReservationList />} />
          <Route path="/reservation/guest" element={<GuestReservation />} />
          <Route path="/reservation/:id" element={<ReservationDetail />} />

          {/* MyPage */}
          <Route path="/mypage/account" element={<MyAccount />} />
          <Route path="/mypage/account/edit" element={<AccountEdit />} />
          <Route path="/mypage/reviews" element={<MyReviews />} />
        </Route>

        {/* Booking flow (no Navbar/Footer) */}
        <Route path="/transfer/:type/booking" element={<BookingSettings />} />
        <Route path="/transfer/:type/payment" element={<TransferPayment />} />
        <Route path="/transfer/:type/complete" element={<TransferComplete />} />
        <Route path="/tour/:id/booking" element={<TourBookingInfo />} />
        <Route path="/tour/:id/payment" element={<TourPayment />} />
        <Route path="/tour/:id/complete" element={<TourComplete />} />
        <Route path="/reservation/:id/surcharge" element={<SurchargePayment />} />

        {/* BugHerd #190: catch-all 404 — robots.txt + SEO noindex 로 인덱싱 차단 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
